import React, { useState, useRef, useCallback } from 'react';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import styles from './SingleSelectFilter.module.css';

export interface SingleSelectOption {
  value: string;
  label: string;
  hint?: string;
}

interface SingleSelectFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: SingleSelectOption[];
  placeholder?: string;
  neutralValue?: string;
  /** Keep the placeholder text in the trigger even when a value is selected. */
  keepPlaceholder?: boolean;
  className?: string;
  wrapperClassName?: string;
}

const SingleSelectFilter: React.FC<SingleSelectFilterProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecione',
  neutralValue,
  keepPlaceholder = false,
  className,
  wrapperClassName,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeDropdown = useCallback(() => setOpen(false), []);

  useOutsideClick(containerRef, closeDropdown);
  useEscapeToClose(closeDropdown, open);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setOpen(false);
  };

  const selectedOption = options.find((o) => o.value === value);
  const isNeutral = neutralValue !== undefined
    ? value === neutralValue
    : !selectedOption;
  const showPlaceholderText = keepPlaceholder || isNeutral;

  const renderOptionLabel = (option: SingleSelectOption) => (
    <>
      {option.label}
      {option.hint ? (
        <span className={styles.optionHint}>{` ${option.hint}`}</span>
      ) : null}
    </>
  );

  const triggerCls = [styles.trigger, open ? styles.triggerOpen : '', className ?? ''].filter(Boolean).join(' ');
  const wrapperCls = [styles.wrapper, wrapperClassName ?? ''].filter(Boolean).join(' ');

  return (
    <div className={wrapperCls} ref={containerRef}>
      <button
        type="button"
        className={triggerCls}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          !isNeutral && selectedOption
            ? `${placeholder}: ${selectedOption.label}`
            : placeholder
        }
      >
        <span className={isNeutral ? styles.triggerPlaceholder : styles.triggerValue}>
          {showPlaceholderText || !selectedOption
            ? placeholder
            : renderOptionLabel(selectedOption)}
        </span>
        <span
          className={`${styles.chevron} ${open ? styles.chevronUp : ''} material-symbols-outlined`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox">
          <ul className={styles.list}>
            {options.map((option) => {
              const active = option.value === value;
              return (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={active}
                  className={[styles.option, active ? styles.optionActive : ''].filter(Boolean).join(' ')}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option.value);
                  }}
                >
                  {renderOptionLabel(option)}
                  {active && (
                    <span
                      className={`${styles.checkIcon} material-symbols-outlined`}
                      aria-hidden="true"
                    >
                      check
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SingleSelectFilter;
